/* eslint-disable indent */
"use client";

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { HelpCircle, Plus, Search } from "lucide-react";
import Link from "next/link";
import LoraCard from "@/components/atoms/LoraCard";
import { SkeletonCard } from "@/components/atoms/Skeletons/LoraCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { fetchAllLoras, fetchAllPublicLoras } from "@/lib/actions";
import { LoraDTO } from "@/types";
export function LoraShowcase() {
  const [showPrivate, setShowPrivate] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loras, setLoras] = useState<LoraDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchLoras = async (isPrivate: boolean) => {
    setIsLoading(true);
    try {
      const fetchedLoras = isPrivate ? await fetchAllLoras() : await fetchAllPublicLoras();
      if (fetchedLoras.loras) {
        setLoras(fetchedLoras.loras);
      }
    } catch (error) {
      console.error("Error fetching LORAs:", error);
      toast.error("Failed to fetch LORAs. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchLoras(showPrivate);
  }, [showPrivate]);
  const handleToggleChange = (checked: boolean) => {
    setShowPrivate(checked);
    fetchLoras(checked);
    toast.success(`Showing ${checked ? "private" : "public"} LORAs`);
  };
  const filteredLoras = loras.filter(lora => {
    const matchesSearch = lora.loraName.toLowerCase().includes(searchQuery.toLowerCase()) || (lora.shortDescription ?? "").toLowerCase().includes(searchQuery.toLowerCase()) || lora.triggerWord.toLowerCase().includes(searchQuery.toLowerCase());

    // Sort by most recently created first
    return matchesSearch;
  }).sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return 0;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  return <div className="space-y-6 suppressHydrationWarning" data-sentry-component="LoraShowcase" data-sentry-source-file="index.tsx">
            <div className="flex flex-col lg:flex-row md:items-center justify-between gap-4 bg-primary p-6 rounded-lg shadow-lg">
                <h1 className="text-4xl font-bold text-gradient">
                    {showPrivate ? "Your" : "Public"} LORAs
                </h1>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Popover data-sentry-element="Popover" data-sentry-source-file="index.tsx">
                            <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="index.tsx">
                                <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-gray-800 rounded-full transition-all duration-200 flex-grow sm:flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                    <p className="text-gray-500">
                                        What is a Lora?
                                    </p>
                                    <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4 !z-[2000] max-w-xs" data-sentry-element="PopoverContent" data-sentry-source-file="index.tsx">
                                <h3 className="text-lg font-semibold text-white mb-2">
                                    What is a LORA?
                                </h3>
                                <p className="text-gray-300 text-sm leading-5">
                                    A LORA (Low-Rank Adaptation) is a customized
                                    model fine-tuned to create specific styles,
                                    characters, or subjects.{" "}
                                </p>
                                <br />
                                <p>
                                    LORAs allow you to influence how images are
                                    generated by providing a set of training
                                    images that teach the model a unique pattern
                                    or concept.
                                </p>
                                <p className="text-gray-400 text-sm leading-5 mt-2">
                                    For example, you could create:
                                </p>
                                <ul className="list-disc pl-5 text-gray-300 text-sm leading-5 mt-2 space-y-1">
                                    <li>
                                        A painting style like watercolor or
                                        cyberpunk.
                                    </li>
                                    <li>
                                        A specific character or face for
                                        storytelling.
                                    </li>
                                    <li>
                                        Detailed objects like swords, gadgets,
                                        or vehicles.
                                    </li>
                                    <li>Fantasy landscapes or environments.</li>
                                </ul>
                                <p className="text-gray-400 text-sm leading-5 mt-2">
                                    Flux.Dev supports creating and using LORAs
                                    for personalized and versatile image
                                    generation. Get creative and bring your
                                    unique ideas to life!
                                </p>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Switch checked={showPrivate} onCheckedChange={handleToggleChange} className="data-[state=checked]:bg-secondary" data-sentry-element="Switch" data-sentry-source-file="index.tsx" />
                        <Label className="text-sm font-medium text-grey-light" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Show my LORAs
                        </Label>
                    </div>
                    <Link title="Create Lora" href="/lora/create" data-sentry-element="Link" data-sentry-source-file="index.tsx">
                        <Button className="!bg-emerald-600 hover:!bg-emerald-700 text-white" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            <Plus className="h-4 w-4 mr-2" data-sentry-element="Plus" data-sentry-source-file="index.tsx" />
                            Create LORA
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="relative max-w-full sm:max-w-md mx-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey" data-sentry-element="Search" data-sentry-source-file="index.tsx" />
                <Input placeholder="Search LORAs..." className="pl-10 bg-primary border-grey-dark focus:border-secondary focus:ring-secondary" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
            </div>

            {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {[...Array(6)].map((_, index) => <SkeletonCard key={index} />)}
                </div> : filteredLoras.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {filteredLoras.map(lora => <LoraCard key={lora.id} lora={lora} />)}
                </div> : <div className="text-center py-8 sm:py-12 bg-primary rounded-lg shadow-lg">
                    <p className="text-grey-light">
                        {searchQuery ? "No LORAs found matching your search" : showPrivate ? "You haven't created any LORAs yet" : "No public LORAs available"}
                    </p>
                    {showPrivate && <Link href="/create-lora" className="inline-block mt-4">
                            <Button className="bg-secondary hover:bg-secondary-dark text-white">
                                Create your first LORA
                            </Button>
                        </Link>}
                </div>}
        </div>;
}